import type { SeriesInstanceUid } from '~/domain/dicom/uids';
import { Box, Typography } from '@mui/material';
import { useIntl } from 'react-intl';
import { PickableImage } from '../adapters/types';
import {
    CANVAS_BG,
    CHROME_BG_HOVER,
    HAIRLINE,
    READOUT,
    READOUT_DIM,
    READOUT_FONT,
} from '../chrome';
import CloseRowButton from '../CloseRowButton';
import PrimaryRowNotice from '../grid/PrimaryRowNotice';
import ImageChoiceMenu from '../tile/ImageChoiceMenu';

interface BindPlaceholderProps {
    availableDatasets: ReadonlyArray<PickableImage>;
    onBind: (seriesInstanceUid: SeriesInstanceUid) => void;
    /** Closes the row; with none, the placeholder has no close control. */
    onClose?: () => void;
    /** Whether closing this row is offered; false for the last row left. */
    canClose?: boolean;
    /**
     * Why the row is empty, said in place of the bind prompt and announced
     * as a status.
     */
    notice?: string;
}

/** The close control in the placeholder's own corner. */
const PLACEHOLDER_CLOSE_SX = {
    width: 20,
    height: 20,
    border: 'none',
    bgcolor: 'transparent',
    p: 0,
    color: READOUT_DIM,
    fontFamily: READOUT_FONT,
    fontSize: 14,
    lineHeight: 1,
    cursor: 'pointer',
    '&:hover': { color: READOUT, bgcolor: CHROME_BG_HOVER },
};

/**
 * The empty-row state: a "bind a dataset" prompt, or the row's notice, above
 * the image menu of the grid. Choosing a series calls `onBind`. Never a
 * passive blank: an unbound row always says what to do.
 */
function BindPlaceholder({
    availableDatasets,
    onBind,
    onClose,
    canClose = false,
    notice,
}: BindPlaceholderProps) {
    const intl = useIntl();
    const disabledReason =
        availableDatasets.length === 0
            ? intl.formatMessage({ id: 'rowBind.noImage' })
            : undefined;

    return (
        <Box
            data-testid="bind-placeholder"
            sx={{
                position: 'relative',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                bgcolor: CANVAS_BG,
                border: '1px dashed',
                borderColor: HAIRLINE,
                color: READOUT_DIM,
                fontFamily: READOUT_FONT,
            }}
        >
            {notice ? (
                <PrimaryRowNotice text={notice} placement="inline" />
            ) : (
                <Typography
                    variant="caption"
                    sx={{ color: READOUT_DIM, letterSpacing: 1, fontSize: 11 }}
                >
                    {intl.formatMessage({ id: 'rowBind.prompt' })}
                </Typography>
            )}
            <ImageChoiceMenu
                placeholder={intl.formatMessage({ id: 'rowBind.placeholder' })}
                images={availableDatasets}
                onChoose={onBind}
                ariaLabel={intl.formatMessage({ id: 'rowBind.name' })}
                testId="bind-image-trigger"
                disabledReason={disabledReason}
            />
            {onClose ? (
                <Box sx={{ position: 'absolute', top: 4, right: 4 }}>
                    {/* The button sits in the placeholder's top right
                        corner: below and inward is open canvas. */}
                    <CloseRowButton
                        canClose={canClose}
                        onClose={onClose}
                        sx={PLACEHOLDER_CLOSE_SX}
                        tooltipPlacement="bottom-end"
                    />
                </Box>
            ) : null}
        </Box>
    );
}

export default BindPlaceholder;
